import { Trans, useTranslation } from "react-i18next";
import { common, input } from "../../../i18n";
import {
  Typography,
  Menu,
  Box,
  MenuItem,
  Divider,
  TableRow,
  IconButton,
  TableCell,
  Collapse,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import dayjs, { Dayjs } from "dayjs";
import React, { useEffect, useState } from "react";
import alertService from "../../../components/alert/alertService";
import { AppDataGrid } from "../../../components/datagrid/AppDataGrid";
import { DROPDOWN_EMISSION_GROUP } from "../../../components/emission/emission-components";
import { ImportFileContext } from "../../../contexts/ImportFileContext";
import ImportFileService from "../../../service/import-file.service";
import { ApiError } from "../../../api/ApiError";
import { tableStyle } from "../../emission/_components/data-table/EmissionDataTableStyle";
import { createExcelDownloadLink } from "../ImportData";
import { StyledTableCell } from "../ImportFileHistoryTable";
import {
  ImportActivitiesHistoryForDataTable,
  ImportedActivity,
  ValidationResult,
} from "../ImportFilePopup.type";

//ICON
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ErrorIcon from "@mui/icons-material/Error";
import { ImportFileErrorTable } from "../ImportFileErrorTable";
import TableApprovalStatus from "../../emission/_components/data-table/TableApprovalStatus";

export const ImportDetailsTable = (props: {
  row: ImportActivitiesHistoryForDataTable;
}) => {
  const { t, i18n } = useTranslation([common, input], {
    nsMode: "fallback",
  });

  const { rows, setRows } = ImportFileContext();

  const { row } = props;

  const [details, setDetail] = useState<ImportedActivity[]>([]);

  const [error, setError] = useState<ValidationResult>();

  const [open, setOpen] = React.useState(false);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const openPopper = Boolean(anchorEl);

  const handleOnClickPopper = (
    event: React.MouseEvent<HTMLButtonElement>,
    row: ImportActivitiesHistoryForDataTable,
  ) => {
    setAnchorEl(event.currentTarget);
    setRowClick(row);
  };

  const handleClose = async (event?: any) => {
    setAnchorEl(null);
  };

  const downloadFile = async (event: any) => {
    handleClose();

    if (rowClick) {
      try {
        const result = await ImportFileService.getImportHistoryFile(
          rowClick?._id,
        );

        if (result) {
          createExcelDownloadLink(result?.filename, result?.excelBase64String);
        }
      } catch (error) {
        if (error instanceof ApiError) return;
        alertService.error("");
      }
    }
  };

  const deleteImportHistory = async (event: any) => {
    handleClose();

    const confirm = await alertService.confirm(
      t("Delete"),
      <Typography variant="text1">
        <span style={{ color: "red" }}>{`${row.filename} `}</span>
        {t("delete-file-text1")}
        <ul style={{ marginTop: 6, marginBottom: 6 }}>
          <li> {`File Name : ${row.filename}`}</li>
          <li>
            {`Uploaded : ${dayjs(row.createdAt)
              .locale(i18n.language)
              .format("DD MMM YYYY")}`}
          </li>
        </ul>
        <Trans
          t={t}
          i18nKey="Are-you-sure-to-delete-file"
          values={{ id: row.id }}
          components={{
            span: <span style={{ color: "var(--red)", fontWeight: "bold" }} />,
          }}
        />
      </Typography>,
    );

    if (rowClick && confirm) {
      alertService.showLoading();
      let result;
      try {
        result = await ImportFileService.deleteImportHistory(row._id);
      } catch {
        // error alert already shown by service
      } finally {
        alertService.closeLoading();
      }
      if (result) {
        setRows([...rows.filter((r) => r._id !== row._id)]);
        alertService.mixin(t("delete-emission-successfully"));
      }
    }
  };

  const [rowClick, setRowClick] =
    React.useState<ImportActivitiesHistoryForDataTable>();

  const columns: GridColDef<ImportedActivity>[] = [
    {
      field: "id",
      headerName: t("ID"),
      disableColumnMenu: true,
      type: "string",
      headerAlign: "left",
      align: "left",
      valueFormatter: (value) => `EM-${value}`,
    },
    {
      field: "startDate",
      headerName: t("Date"),
      disableColumnMenu: true,
      headerAlign: "left",
      type: "date",
      minWidth: 100,
      valueFormatter: (value, row) => {
        const start: Dayjs = dayjs(value);

        const end: Dayjs = dayjs(row.endDate);

        if (start.isSame(end, "month")) {
          return start.locale(i18n.language).format("MMM YYYY");
        } else {
          return `${start.locale(i18n.language).format("MMM YYYY")} -\n${end
            .locale(i18n.language)
            .format("MMM YYYY")}`;
        }
      },
      renderCell: (params) => {
        return (
          <Typography variant="text1" sx={{ whiteSpace: "pre-line" }}>
            {params.formattedValue}
          </Typography>
        );
      },
    },
    {
      field: "suborganizationRef",
      headerName: t("Sub-Organizations"),
      headerAlign: "left",
      flex: 1,
      disableColumnMenu: true,
      valueFormatter: (value, row) => {
        return row.suborganizationName;
      },
    },
    {
      field: "siteName",
      headerName: t("Site"),
      headerAlign: "left",
      disableColumnMenu: true,
      flex: 1,
      valueFormatter: (value, row) => {
        return row.siteName;
      },
    },

    {
      field: "assetName",
      headerName: t("Asset"),
      headerAlign: "left",
      disableColumnMenu: true,
      getApplyQuickFilterFn: undefined,
      valueFormatter: (value, row) => {
        return row.assetName;
      },
      flex: 1,
    },
    {
      field: "emissionFactorRef",
      headerName: t("Emission-Group"),
      headerAlign: "left",
      disableColumnMenu: true,
      valueFormatter: (value, row) => {
        const item = DROPDOWN_EMISSION_GROUP.find(
          (e) => e.emissionGroup === row.emissionGroup,
        );

        return item ? t(item.groupEn) : t(row.emissionGroup);
      },
      flex: 1,
    },
    {
      field: "assetType",
      headerName: t("Emission-Source"),
      headerAlign: "left",
      disableColumnMenu: true,
      valueFormatter: (value, row) => {
        return i18n.language === "th" ? row.attributeTh : row.attributeEn;

        // return i18n.language.toLowerCase().includes("en")
        //   ? row.emissionFactorRef.attributeEn
        //   : row.emissionFactorRef.attributeTh;
      },
      flex: 1,
    },
    {
      field: "amount",
      headerName: t("Amount"),
      headerAlign: "left",
      sortable: true,
      disableColumnMenu: true,
      editable: false,
      type: "number",
      getApplyQuickFilterFn: undefined,
      align: "left",
    },
    {
      field: "unit",
      headerName: t("Unit"),
      sortable: false,
      headerAlign: "left",
      disableColumnMenu: true,
      getApplyQuickFilterFn: undefined,
      valueFormatter: (value, row) => {
        return row.unit;
      },
    },
    {
      field: "status",
      headerName: t("Status"),
      disableColumnMenu: true,
      width: 100,
      headerAlign: "center",
      renderCell: (params) => (
        <TableApprovalStatus status={params.value} by={""} />
      ),
    },
  ];

  const _getImportHistoryDetail = async (_id: string): Promise<void> => {
    let details;
    try {
      details = await ImportFileService.getImportHistoryDetail(row._id);
    } catch {
      // error alert already shown by service
      return;
    }

    if (row.status === "importedSuccess")
      setDetail((details as ImportedActivity[]) ?? []);

    if (row.status === "validatedFail") setError(details as ValidationResult);
  };

  useEffect(() => {
    if (!open) return;

    if (error || details.length) return;

    if (row.status === "importedSuccess" || row.status === "validatedFail")
      _getImportHistoryDetail(row._id);
  }, [open]);

  return (
    <React.Fragment>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={openPopper}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        key={`${row._id}-basic-menu`}
      >
        {row.status.toLowerCase() === "validatedsuccess" ? (
          <Box>
            <MenuItem onClick={downloadFile} id="download" disabled>
              {t("Import-Data")}
            </MenuItem>
            <Divider variant="middle" component="li" />
          </Box>
        ) : row.status.toLowerCase() === "importedsuccess" ? (
          <Box>
            <MenuItem onClick={downloadFile} id="download">
              {t("Download")}
            </MenuItem>
            <Divider variant="middle" component="li" />
          </Box>
        ) : undefined}

        <MenuItem
          id="deleted"
          onClick={deleteImportHistory}
          style={{ color: "red" }}
        >
          {t("Delete")}
        </MenuItem>
      </Menu>

      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        <StyledTableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </StyledTableCell>
        <StyledTableCell>{row.id}</StyledTableCell>
        <StyledTableCell>{row.filename}</StyledTableCell>
        <StyledTableCell>
          {row.status === "importedSuccess" ? (
            <CheckCircleIcon color="primary" />
          ) : row.status === "validatedFail" ? (
            <CancelIcon color="error" />
          ) : (
            <ErrorIcon color="warning" />
          )}
        </StyledTableCell>
        <StyledTableCell>
          {dayjs(row.createdAt).locale(i18n.language).format("DD MMM YYYY")}
        </StyledTableCell>
        <StyledTableCell>{row.importedBy}</StyledTableCell>
        <StyledTableCell>
          <IconButton onClick={(event) => handleOnClickPopper(event, row)}>
            <img src="/img/more.svg" alt="icon-more" />
          </IconButton>
        </StyledTableCell>
      </TableRow>
      <TableRow>
        <TableCell
          style={{
            paddingBottom: 0,
            paddingTop: 0,
          }}
          colSpan={7}
        >
          <Collapse
            in={open}
            timeout="auto"
            unmountOnExit
            sx={{ width: "100%" }}
          >
            {row.status.toLowerCase() === "importedsuccess" ? (
              <Box sx={{ margin: 1 }}>
                <Box width={"100%"}>
                  <AppDataGrid
                    key={`table-${row._id}`}
                    rows={details}
                    columns={columns}
                    rowHeight={60}
                    autoHeight
                    initialState={{
                      pagination: {
                        paginationModel: { page: 0, pageSize: 5 },
                      },
                    }}
                    autosizeOnMount
                    sx={tableStyle("light")}
                  />
                </Box>
              </Box>
            ) : row.status.toLowerCase() === "validatedsuccess" ? (
              <Box
                display={"flex"}
                flexDirection={"row"}
                gap={"8px"}
                alignItems={"center"}
                sx={{
                  paddingY: "16px",
                  paddingX: "8px",
                }}
              >
                <ErrorIcon color="warning" />
                <Typography variant="text1" color={"var(--dark)"}>
                  {t("Validated-Success-Description")}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ margin: 1 }}>
                <ImportFileErrorTable rows={error?.errorDetails ?? []} />
              </Box>
            )}
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};
